import { createRouter, createWebHistory } from "vue-router";
import Dashboard from "../views/Dashboard.vue";
import Billing from "../views/Account/Billing.vue";
import Profile from "../views/Account/Profile.vue";
import Signup from "../views/Auth/Signup.vue";
import Signin from "../views/Auth/Signin.vue";

import { useAuthStore } from "@/store/AuthStore";

import NotFound from "@/views/Error/NotFound.vue";
import Unauthorized from "@/views/Error/Unauthorized.vue";
import NewSensor from "@/views/Sensors/NewSensor.vue";
import ListSensors from "@/views/Sensors/ListSensors.vue";
import Tables from "@/views/Visualise/Tables.vue";
import Graphs from "@/views/Visualise/Graphs.vue";
import GetStarted from "@/views/Account/GetStarted.vue";
import ViewSensor from "@/views/Sensors/ViewSensor.vue";
import AdminDashboard from "@/views/Admin/AdminDashboard.vue";
import DeviceList from "@/views/Devices/DeviceList.vue";
import DeviceDetail from "@/views/Devices/DeviceDetail.vue";

import NProgress from "nprogress";

const routes = [
  { // 403 Unauthorized
    path: '/unauthorized',
    name: 'Unauthorized',
    component: Unauthorized,
    meta: { layout: 'auth' }
  },
  { // Error routes
    path: '/:pathMatch(.*)*',
    component: NotFound
  },
  { // Redirect / to dash
    path: "/",
    name: "/",
    redirect: "/dashboard",
  },
  { // Dash
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
    meta: {
      requiresAuth: true,
      layout: 'default'
    }
  },
  { // Sensor Routes
    path: "/sensor/add",
    name: "Add new sensor",
    component: NewSensor,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/sensor/manage",
    name: 'Manage Sensors',
    component: ListSensors,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/sensor/manage/:unique_id",
    name: 'Edit Sensor',
    component: ViewSensor,
    props: true,
    meta: {
      requiresAuth: true
    }
  },
  { // Visualise routes
    path: "/visualise/tables",
    name: "Tables",
    component: Tables,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/visualise/graphs",
    name: "Graphs",
    component: Graphs,
    meta: {
      requiresAuth: true
    }
  },
  { // Account routes
    path: "/account/billing",
    name: "Billing",
    component: Billing,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/account",
    name: "My Account",
    component: Profile,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/account/get-started",
    name:"Get Started",
    component: GetStarted,
    meta: {
      requiresAuth: true
    }
  },
  { // Alert routes
    path: "/alerts/conditions",
    name: "Alert Conditions",
    component: () => import('@/views/Alerts/AlertConditions.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/alerts/flows",
    name: "Alert Flows",
    component: () => import('@/views/Alerts/AlertFlows.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/login-test",
    name: "Login Test",
    component: () => import('@/views/Auth/Signin.vue'),
    meta: { layout: 'auth' }
  },
  {
    path: "/gradient",
    name: "gradient",
    component: () => import('@/views/Auth/Gradient.vue'),
    meta: { layout: 'auth' }
  },
  { // Auth rotues
    path: "/signin", // TODO : Change path? /auth/login?
    name: "Signin",
    component: Signin,
    meta: { layout: 'auth' }
  },
  {
    path: "/signup",
    name: "Signup",
    component: Signup,
    meta: { layout: 'auth' }
  },
  {
    path: '/logout',
    name: 'Logout',
    component: () => import('@/views/Auth/LoggedOut.vue'),
    meta: { layout: 'auth' }
  },
  {
    path: '/login/approval',
    name: 'Login Approval',
    component: () => import('@/views/Auth/VerifyCode.vue'),
    meta: { layout: 'auth' }
  },
  {
    path: '/admin',
    name: 'Admin Dashboard',
    component: AdminDashboard,
    meta: {
      requiresAdmin: true,
      requiresAuth: true,
    },
  },
  {
    path: '/admin/sensor-types',
    name: 'Admin Sensor Types',
    component: AdminDashboard,
    meta: {
      requiresAdmin: true,
      requiresAuth: true,
    },
  },
  {
    path: '/admin/users',
    name: 'Admin Users',
    component: AdminDashboard,
    meta: {
      requiresAdmin: true,
      requiresAuth: true,
    },
  },
  {
    path: '/admin/settings',
    name: 'Admin Settings',
    component: AdminDashboard,
    meta: {
      requiresAdmin: true,
      requiresAuth: true,
    },
  },
  // Device routes
  {
    path: '/devices',
    name: 'Device List',
    component: DeviceList,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/devices/:id',
    name: 'Device Detail',
    component: DeviceDetail,
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

router.beforeResolve((to, from, next) => {
  if (to.name) {
    NProgress.start();
  }
  next();
});

router.afterEach( () => {
  NProgress.done();
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  
  // Check requiresAdmin first (more specific)
  if (to.matched.some((record) => record.meta.requiresAdmin)) {
    if (!authStore.isAuthenticated) {
      next('/signin');
    } else if (!authStore.isAdmin) {
      next('/unauthorized');
    } else {
      next();
    }
  } else if (to.matched.some((record) => record.meta.requiresAuth)) {
    // Check for requiresAuth meta
    if (authStore.isAuthenticated) {
      next();
    } else {
      next('/signin');
    }
  } else if (to.matched.some((record) => record.name === 'Signin') && authStore.isAuthenticated) {
    // Don't allow a user to access login if authenticated
    next('/dashboard');
  } else {
    next();
  }
})

export default router;
